import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { useDevBypassRedirect } from "@/hooks/use-dev-bypass-redirect";
import { NanaeLogoSymbol } from "@/components/qv3/NanaeLogo";
import { useAuth } from "@/contexts/AuthContext";
import { socialMeta } from "@/lib/seo-meta";

const searchSchema = z.object({
  email: z.string().optional(),
  // "ambiguous" = email déjà inscrit (peut-être confirmé, peut-être pas) :
  // Supabase ne le distingue pas, on affiche un message qui couvre les deux cas.
  state: z.enum(["new", "ambiguous"]).optional(),
});

export const Route = createFileRoute("/email-confirmation")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [{ title: "Vérification — Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Vérification — Hōjin",
        description: "Vérification — Hōjin",
        url: "https://hojin.ai/email-confirmation",
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/email-confirmation" }],
  }),
  component: EmailConfirmationPage,
});

export function EmailConfirmationPage() {
  useDevBypassRedirect();
  const { email, state } = Route.useSearch();
  const ambiguous = state === "ambiguous";
  const auth = useAuth() as unknown as { resendConfirmation?: (e: string) => Promise<void> };
  const [resendState, setResendState] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [resendErr, setResendErr] = useState<string | null>(null);

  const onResend = async () => {
    if (!email || !auth.resendConfirmation) return;
    setResendState("loading");
    setResendErr(null);
    try {
      await auth.resendConfirmation(email);
      setResendState("ok");
    } catch (e) {
      setResendErr(e instanceof Error ? e.message : "Échec de l'envoi");
      setResendState("err");
    }
  };

  return (
    <div className="nanae-auth-shell qv3 qv3-dark">
      <main className="nanae-auth-main">
        <section className="nanae-auth-card" aria-labelledby="verify-title">
          <span className="nanae-auth-card-corner left">VÉRIFICATION · EN COURS</span>
          <span className="nanae-auth-card-corner right">
            <span className="qv3-led" aria-hidden="true" />
            PENDING
          </span>

          <div className="nanae-auth-logo">
            <span className="nanae-lotus-breath" style={{ display: "inline-block" }}>
              <NanaeLogoSymbol size="xl" />
            </span>
          </div>

          <p className="nanae-auth-eyebrow">§ VÉRIFICATION · MMXXVI</p>
          <h1 id="verify-title" className="nanae-auth-title">
            Un email vous a été envoyé.
          </h1>
          <p className="nanae-auth-subtitle">
            {email ? (
              <>Vérifiez la boîte de réception de <strong style={{ color: "var(--qv3-ink)" }}>{email}</strong>.</>
            ) : (
              "Vérifiez votre boîte de réception."
            )}
          </p>

          <div
            style={{
              padding: 16,
              border: "1px solid var(--qv3-grey-100)",
              fontFamily: "var(--qv3-font-serif)",
              fontStyle: "italic",
              fontSize: 15,
              lineHeight: 1.55,
              color: "var(--qv3-ink)",
              marginBottom: 20,
            }}
          >
            {ambiguous ? (
              <>
                Cet email est déjà enregistré. S'il n'a pas encore été confirmé, un nouveau lien vient d'être envoyé.
                S'il était déjà confirmé,{" "}
                <Link to="/login" style={{ color: "var(--accent-cramoisi, #000000)", textDecoration: "underline" }}>
                  connectez-vous
                </Link>
                .
              </>
            ) : (
              <>Confirmez votre adresse en cliquant sur le lien reçu. Le Conseil ne s'ouvre qu'aux visages connus.</>
            )}
          </div>

          {auth.resendConfirmation && (
            <button
              type="button"
              className="qv3-btn-hifi is-ghost"
              onClick={onResend}
              disabled={resendState === "loading" || !email}
            >
              {resendState === "loading"
                ? "Envoi…"
                : resendState === "ok"
                  ? "Email renvoyé ✓"
                  : "Renvoyer l'email"}
            </button>
          )}

          {resendErr && (
            <p role="alert" className="nanae-auth-error" style={{ marginTop: 14 }}>
              <span className="qv3-led" aria-hidden="true" />
              {resendErr}
            </p>
          )}

          <div className="nanae-auth-altlinks">
            <Link to="/login" className="nanae-auth-altlink">
              ← Retour à la connexion
            </Link>
          </div>
        </section>
      </main>
      <footer className="nanae-auth-foot" data-rp="auth-footer">SEPT VOIX, UNE DIRECTION · MMXXVI</footer>
    </div>
  );
}
